import { useState } from "react";
import { ArrowRight, Building2, Check, Clock3, ShieldCheck, Sparkles, UserRound } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { tt } from "../lib/i18n";
import { useSession } from "../lib/useSession";
import { openPaddleCheckout } from "../lib/paddle";

// FireVibe'ın gerçek src/routes/pricing.tsx dosyasından taşınmıştır.
//
// DÜZELTME (2. QA denetimi, F-01 — CRITICAL): ödeme/checkout artık Paddle.js
// ile bağlı (bkz. ../lib/paddle.ts). Fiyat ID'leri ortam değişkenlerinden
// okunur, uydurulmadı — doldurulmadıkları sürece ilgili plan butonu "yakında"
// durumunda kalır ve checkout açmaz.

type PlanSlug = "personal" | "solo" | "studio";
type Plan = { slug?: PlanSlug; name: string; price: string; description: string; features: { tr: string; en: string }[]; icon: React.ReactNode; featured?: boolean };

const plans: Plan[] = [
  {
    name: "Free start",
    price: "$0",
    description: tt({ tr: "Hesap açınca 20 AI sorgusu dahil · ödeme yok", en: "20 AI queries included when you create an account · no payment" }),
    icon: <Clock3 size={20} />,
    features: [
      { tr: "Temel takvim ve randevu akışı", en: "Core calendar and booking flow" },
      { tr: "Kişisel veya işletme alanı", en: "Personal or business space" },
      { tr: "20 AI sorgusu · ilk kurulum", en: "20 AI queries · first setup" },
    ],
  },
  {
    slug: "personal",
    name: "Personal",
    price: "$2.99",
    description: tt({ tr: "Kişisel planlama için hafif ve güçlü.", en: "Lightweight and powerful personal planning." }),
    icon: <UserRound size={20} />,
    features: [
      { tr: "Sınırsız kişisel randevu ve görev", en: "Unlimited personal appointments and tasks" },
      { tr: "Akıllı hatırlatıcılar ve hızlı destek", en: "Smart reminders and fast assistance" },
      { tr: "1.000 AI sorgusu / ay", en: "1,000 AI queries / month" },
    ],
  },
  {
    slug: "solo",
    name: "Solo",
    price: "$4.99",
    description: tt({ tr: "Tek kişilik hizmet işletmeleri için.", en: "For one-person service businesses." }),
    icon: <Sparkles size={20} />,
    featured: true,
    features: [
      { tr: "Tek kişi işletmesi için online rezervasyon", en: "Online booking for one-person businesses" },
      { tr: "Müşteri notları ve otomatik hatırlatmalar", en: "Client notes and automated reminders" },
      { tr: "2.000 AI sorgusu / ay", en: "2,000 AI queries / month" },
    ],
  },
  {
    slug: "studio",
    name: "Studio",
    price: "$12.99",
    description: tt({ tr: "Büyüyen ekipler için hızlı çalışma alanı.", en: "A fast workspace for growing teams." }),
    icon: <Building2 size={20} />,
    features: [
      { tr: "Ekip takvimi ve rol bazlı çalışma", en: "Team calendar and role-based workspace" },
      { tr: "Hizmet, müşteri ve yoğunluk görünümü", en: "Services, clients and capacity view" },
      { tr: "3.000 AI sorgusu / ay", en: "3,000 AI queries / month" },
    ],
  },
];

export function PricingScreen() {
  const { session } = useSession();
  const navigate = useNavigate();
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [checkoutNotice, setCheckoutNotice] = useState<string | null>(null);

  async function handlePlanClick(plan: Plan) {
    if (!plan.slug) return; // Free start — zaten /auth'a gidiyor (aşağıdaki Link)
    if (!session) {
      navigate("/"); // oturum yok — önce giriş/kayıt
      return;
    }
    setCheckoutError(null);
    setCheckoutNotice(null);
    const result = await openPaddleCheckout(plan.slug);
    if (result.ok && result.changed) {
      setCheckoutNotice(
        result.direction === "downgrade"
          ? tt({ tr: "Planın düşürülüyor; fark bir sonraki yenilemede hesaplanır.", en: "Your plan is being downgraded; the difference is applied at your next renewal." })
          : tt({ tr: "Planın yükseltiliyor; birkaç saniye içinde etkinleşir.", en: "Your plan is being upgraded; it takes effect within seconds." })
      );
      return;
    }
    if (result.ok && result.scheduled) {
      const d = result.effectiveAt ? new Date(result.effectiveAt).toLocaleDateString() : "";
      setCheckoutNotice(tt({
        tr: `Plan düşürme ${d} tarihinde (dönem sonu) uygulanacak; o zamana kadar mevcut planını kullanırsın.`,
        en: `Your plan will be downgraded on ${d} (end of period); you keep your current plan until then.`,
      }));
      return;
    }
    if (result.ok && result.pendingCleared) {
      setCheckoutNotice(tt({ tr: "Planlanan düşürme iptal edildi; mevcut planında devam ediyorsun.", en: "The scheduled downgrade was cancelled; you stay on your current plan." }));
      return;
    }
    if (!result.ok) {
      const messages: Record<string, string> = {
        "not-configured": tt({ tr: "Bu plan için ödeme yakında açılacak.", en: "Payment for this plan is coming soon." }),
        "anonymous-user": tt({ tr: "Satın almak için Google veya e-posta ile giriş yapmalısın (Demo hesabıyla olmaz).", en: "Sign in with Google or email to subscribe (not possible with a Demo account)." }),
        "same-plan": tt({ tr: "Bu plana zaten abonesin.", en: "You are already subscribed to this plan." }),
        "payment-issue": tt({ tr: "Aboneliğinde ödeme sorunu var; plan değiştirmeden önce ödeme yöntemini güncellemelisin.", en: "Your subscription has a payment issue; update your payment method before changing plans." }),
        paused: tt({ tr: "Aboneliğin duraklatılmış; plan değiştirilemez.", en: "Your subscription is paused; the plan can't be changed." }),
        "scheduled-change-pending": tt({ tr: "Aboneliğinde bekleyen bir iptal/değişiklik var; plan şimdi değiştirilemez.", en: "Your subscription has a pending cancellation/change; the plan can't be changed now." }),
        "period-unknown": tt({ tr: "Dönem sonu tarihi alınamadı; plan düşürme şimdi yapılamıyor, daha sonra tekrar dene.", en: "Couldn't determine the period end; the downgrade can't be scheduled now, try later." }),
        "checkout-in-progress": tt({ tr: "Ödeme ekranı hazırlanıyor, birkaç saniye sonra tekrar dene.", en: "Checkout is being prepared, try again in a few seconds." }),
        "payment-processing": tt({ tr: "Ödemen işleniyor; birkaç dakika içinde planın etkinleşir. Tekrar ödeme yapma.", en: "Your payment is being processed; your plan activates within minutes. Do not pay again." }),
      };
      setCheckoutError(messages[result.reason ?? ""] ?? tt({ tr: "Ödeme ekranı açılamadı, lütfen tekrar dene.", en: "Couldn't open checkout, please try again." }));
    }
  }

  return (
    <main className="pricing-page">
      <header className="pricing-header">
        <Link to="/" className="auth-brand">
          plan<span>moy</span>
          <i />
        </Link>
        <Link to="/" className="pricing-signin">
          {tt({ tr: "Zaten hesabın var mı? Giriş yap", en: "Already have an account? Sign in" })}
        </Link>
      </header>

      <section className="pricing-hero">
        <p className="eyebrow">{tt({ tr: "Sade ve ulaşılabilir planlar", en: "Simple, accessible plans" })}</p>
        <h1>{tt({ tr: "İşine ve akışına uygun planı seç.", en: "Choose the plan for your work and flow." })}</h1>
        <p>{tt({ tr: "Kişisel kullanımda güçlü bir başlangıç; işletmelerde hızlı ve net randevu yönetimi. Kart gerekmez.", en: "A strong personal start, with fast and clear booking management for businesses. No card required." })}</p>
        <div className="trial-badge">
          <ShieldCheck size={16} />
          <strong>{tt({ tr: "İlk kurulum ücretsiz", en: "Free first setup" })}</strong>
          <span>{tt({ tr: "Hesap açınca 20 AI sorgusu dahil · ödeme yok", en: "20 AI queries included when you create an account · no payment" })}</span>
        </div>
      </section>

      <section className="pricing-grid">
        {plans.map((plan) => (
          <article key={plan.name} className={`pricing-card ${plan.featured ? "featured" : ""}`}>
            <div className="pricing-card-top">
              <span className="pricing-icon">{plan.icon}</span>
              {plan.featured && <span className="pricing-popular">{tt({ tr: "En iyi başlangıç", en: "Best starting point" })}</span>}
            </div>
            <h2>{plan.name}</h2>
            <p className="pricing-description">{plan.description}</p>
            <div className="pricing-price">
              <strong>{plan.price}</strong>
              <span>{tt({ tr: "aylık", en: "monthly" })}</span>
            </div>
            <ul>
              {plan.features.map((f) => (
                <li key={f.tr}>
                  <Check size={14} />
                  {tt(f)}
                </li>
              ))}
            </ul>
            {plan.slug ? (
              <button type="button" className="pricing-cta" onClick={() => handlePlanClick(plan)}>
                {tt({ tr: "Bu planla başla", en: "Start with this plan" })}
                <ArrowRight size={15} />
              </button>
            ) : (
              <Link to="/" className="pricing-cta">
                {tt({ tr: "Ücretsiz başla", en: "Start free" })}
                <ArrowRight size={15} />
              </Link>
            )}
          </article>
        ))}
      </section>

      {checkoutNotice && (
        <p className="pricing-note" role="status">
          {checkoutNotice}
        </p>
      )}

      {checkoutError && (
        <p className="pricing-note" role="alert" style={{ color: "#e5484d" }}>
          {checkoutError}
        </p>
      )}

      <section className="pricing-ai-note">
        <Sparkles size={18} />
        <div>
          <strong>{tt({ tr: "AI sorgusu", en: "AI queries" })}</strong>
          <p>{tt({ tr: "Her AI sorgusu planındaki kotadan düşer.", en: "Every AI query uses your plan allowance." })}</p>
        </div>
      </section>

      <p className="pricing-note">
        <b>{tt({ tr: "Kotalar ve farklar: ", en: "Limits and differences: " })}</b>
        {tt({
          tr: "Ödeme Paddle üzerinden güvenle alınır. Kartına sadece seçtiğin planın ücreti yansıtılır.",
          en: "Payments are securely processed via Paddle. Only your chosen plan's price is charged to your card.",
        })}{" "}
        <Link to="/legal">{tt({ tr: "Kullanım koşulları ve gizlilik", en: "Terms and privacy" })}</Link>
      </p>
    </main>
  );
}